"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, Search, Check, Users, Crown } from "lucide-react";
import { cn } from "@/lib/utils";

interface UserSelectorProps {
  label?: string;
  value?: string | string[];
  onValueChange: (userId: string | string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  multiple?: boolean;
  required?: boolean;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  type: "individual" | "group";
  count?: number;
  description?: string;
}

const USER_OPTIONS: UserOption[] = [
  {
    id: "all_users",
    name: "All Users",
    email: "Everyone on your customer list",
    type: "group",
    count: 2450,
    description: "Send to all customers in your database",
  },
  {
    id: "premium_customers",
    name: "Premium Customers",
    email: "Customers with VIP status",
    type: "group",
    count: 350,
    description: "High-value customers with premium status",
  },
  {
    id: "active_shoppers",
    name: "Active Shoppers",
    email: "Customers who purchased in last 30 days",
    type: "group",
    count: 1200,
    description: "Recently active customers",
  },
  {
    id: "new_customers",
    name: "New Customers",
    email: "Customers who joined in last 7 days",
    type: "group",
    count: 85,
    description: "Recently registered customers",
  },
  {
    id: "john_doe",
    name: "John Doe",
    email: "john.doe@example.com",
    type: "individual",
    description: "Individual customer",
  },
  {
    id: "jane_smith",
    name: "Jane Smith",
    email: "jane.smith@example.com",
    type: "individual",
    description: "Individual customer",
  },
  {
    id: "mike_johnson",
    name: "Mike Johnson",
    email: "mike.johnson@example.com",
    type: "individual",
    description: "Individual customer",
  },
];

export const UserSelector: React.FC<UserSelectorProps> = ({
  label = "Select Recipients",
  value = "",
  onValueChange,
  placeholder = "Search and select users",
  error,
  helperText,
  multiple = false,
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedValues = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value
      ? [value]
      : [];
  const selectedUsers = USER_OPTIONS.filter((user) =>
    selectedValues.includes(user.id),
  );

  const filteredUsers = USER_OPTIONS.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleUserSelect = (user: UserOption) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(user.id);

      if (isSelected) {
        // Remove from selection
        const newValues = currentValues.filter((id) => id !== user.id);
        onValueChange(newValues);
      } else {
        // Add to selection
        const newValues = [...currentValues, user.id];
        onValueChange(newValues);
      }
    } else {
      onValueChange(user.id);
      setOpen(false);
    }
    setSearchQuery("");
  };

  const getUserIcon = (user: UserOption) => {
    if (user.type === "group") {
      return user.id === "premium_customers" ? (
        <Crown className="h-4 w-4" />
      ) : (
        <Users className="h-4 w-4" />
      );
    }
    return (
      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
        <span className="text-white font-medium text-sm">
          {user.name.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto min-h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <div className="flex-1 text-left">
                {selectedUsers.length > 0 ? (
                  multiple && selectedUsers.length > 1 ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {selectedUsers.slice(0, 2).map((user, index) => (
                          <div key={user.id} className="flex items-center">
                            {getUserIcon(user)}
                            {index < 1 && selectedUsers.length > 1 && (
                              <span className="text-xs text-muted-foreground mx-1">
                                +
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">
                          {selectedUsers.length === 2
                            ? `${selectedUsers[0].name} + 1 other`
                            : `${selectedUsers[0].name} + ${selectedUsers.length - 1} others`}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {selectedUsers.length} recipients selected
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {getUserIcon(selectedUsers[0])}
                      <div className="flex-1">
                        <div className="font-medium">
                          {selectedUsers[0].name}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          {selectedUsers[0].type === "group" &&
                            selectedUsers[0].count && (
                              <>
                                <Users className="h-3 w-3" />
                                {selectedUsers[0].count.toLocaleString()} users
                              </>
                            )}
                          {selectedUsers[0].type === "individual" &&
                            selectedUsers[0].email}
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-secondary-background text-foreground border border-border rounded-b-xl shadow-[0_10px_15px_-3px_rgba(0,0,0,0.4),0_4px_6px_-2px_rgba(0,0,0,0.25)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.8),0_4px_6px_-2px_rgba(0,0,0,0.6)]"
            align="start"
            style={{ width: "var(--radix-popover-trigger-width)" }}
          >
            <div className="flex items-center border-b border-border px-3 bg-secondary-background">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                placeholder="Search users or groups..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto overflow-x-hidden bg-secondary-background rounded-b-xl">
              <div className="p-1 flex flex-col gap-[5px]">
                {filteredUsers.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No users found.
                  </div>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelected = selectedValues.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        className={cn(
                          "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-3 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                          isSelected && "bg-accent text-accent-foreground",
                        )}
                        onClick={() => handleUserSelect(user)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 text-primary",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="flex items-center gap-3 flex-1">
                          {getUserIcon(user)}
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">{user.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-muted-foreground truncate">
                                {user.email}
                              </p>
                              {user.type === "group" && user.count && (
                                <>
                                  <span className="text-xs text-muted-foreground">
                                    •
                                  </span>
                                  <p className="text-xs text-muted-foreground">
                                    {user.count.toLocaleString()} users
                                  </p>
                                </>
                              )}
                            </div>
                            {user.description && (
                              <p className="text-xs text-muted-foreground mt-1 opacity-75">
                                {user.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {multiple && selectedValues.length > 0 && (
                <div className="border-t border-border p-2 bg-secondary-background">
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => setOpen(false)}
                  >
                    Done ({selectedValues.length} selected)
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export default UserSelector;
